import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import InvestmentRetirementApp from './InvestmentRetirementApp';
import { AuthProvider, useAuth } from './AuthContext';
import { CountryProvider, useCountry } from './CountryContext';
import { ThemeProvider } from './ThemeContext';
import LandingPage from './LandingPage';
import Login from './Login';
import Signup from './Signup';
import PasswordReset from './PasswordReset';
import LegalAcceptance from './components/legal/LegalAcceptance';
import OnboardingWizard from './components/OnboardingWizard';
import PrivacyPolicyPage from './pages/PrivacyPolicyPage';
import TermsPage from './pages/TermsPage';
import PrivacyFirstPortfolioTrackerPage from './pages/PrivacyFirstPortfolioTrackerPage';
import SippCalculatorPage from './pages/SippCalculatorPage';
import UKPensionCalculatorPage from './pages/UKPensionCalculatorPage';
import USRetirementCalculatorPage from './pages/USRetirementCalculatorPage';
import FinancialPlannerVsSoftwarePage from './pages/FinancialPlannerVsSoftwarePage';
import './App.css';

function AppContentInner() {
  const { currentUser, userProfile } = useAuth();
  const { setSelectedCountry } = useCountry();
  const [showLanding, setShowLanding] = useState(true);
  const [authView, setAuthView] = useState('login'); // 'login', 'signup', 'reset'
  const [selectedCountryForAuth, setSelectedCountryForAuth] = useState(null);
  const [showLegalAcceptance, setShowLegalAcceptance] = useState(false);
  const [legalAccepted, setLegalAccepted] = useState(false);
  const [showOnboarding, setShowOnboarding] = useState(false);

  const handleCountrySelected = (countryCode) => {
    console.log('Country selected on landing page:', countryCode);
    setSelectedCountryForAuth(countryCode);
    setSelectedCountry(countryCode);
  };

  const handleBackToApp = () => {
    setShowLanding(false);
  };

  const handleNavigateToLanding = () => {
    setShowLanding(true);
  };

  // Check if user has accepted legal terms
  useEffect(() => {
    if (currentUser) {
      // User is logged in - ensure we're not showing the landing page
      setShowLanding(false);

      try {
        const acceptance = localStorage.getItem('legal_acceptance');
        if (acceptance) {
          const acceptanceData = JSON.parse(acceptance);
          // ✅ FIX: Check if acceptance matches current user AND terms are accepted
          if (acceptanceData.email === currentUser.email && acceptanceData.termsAccepted) {
            console.log('[Legal Check] Found valid acceptance in localStorage for:', currentUser.email);
            setLegalAccepted(true);
            setShowLegalAcceptance(false);
          } else {
            // Different user or terms not accepted - show acceptance modal
            console.log('[Legal Check] No valid acceptance found, showing modal');
            setShowLegalAcceptance(true);
            setLegalAccepted(false);
          }
        } else {
          // No acceptance found, show modal for new users
          console.log('[Legal Check] No acceptance in localStorage, showing modal');
          setShowLegalAcceptance(true);
          setLegalAccepted(false);
        }
      } catch (error) {
        console.error('Error checking legal acceptance:', error);
        setShowLegalAcceptance(true);
        setLegalAccepted(false);
      }
    } else {
      // User logged out - reset states
      console.log('[Legal Check] No user, resetting states');
      setLegalAccepted(false);
      setShowLegalAcceptance(false);
      setShowOnboarding(false);
    }
  }, [currentUser]);

  // Load user's country from their profile when they log in
  useEffect(() => {
    if (currentUser && userProfile && userProfile.country) {
      console.log('Loading country from user profile:', userProfile.country);
      setSelectedCountry(userProfile.country);
    } else {
      console.log('No country in profile or no user/profile', { currentUser: !!currentUser, userProfile: !!userProfile, country: userProfile?.country });
    }
  }, [currentUser, userProfile, setSelectedCountry]);

  const handleLegalAcceptance = (acceptanceData) => {
    console.log('[Legal Acceptance] Terms accepted:', acceptanceData);
    console.log('[Legal Acceptance] Current user profile:', {
      email: userProfile?.email,
      country: userProfile?.country,
      onboardingCompleted: userProfile?.onboardingCompleted
    });
    setLegalAccepted(true);
    setShowLegalAcceptance(false);
  };

  // Check if user needs to complete onboarding
  useEffect(() => {
    console.log('[Onboarding Check] Checking conditions:', {
      hasUser: !!currentUser,
      hasProfile: !!userProfile,
      legalAccepted,
      onboardingStatus: userProfile?.onboardingCompleted
    });

    if (currentUser && userProfile && legalAccepted) {
      // Show onboarding for new users who haven't completed it
      // Check !== true to catch both false and undefined cases
      if (userProfile.onboardingCompleted !== true) {
        console.log('[Onboarding Check] ✅ Showing wizard - onboarding not completed');
        setShowOnboarding(true);
      } else {
        console.log('[Onboarding Check] ⏭️ Skipping wizard - onboarding already completed');
        setShowOnboarding(false);
      }
    } else {
      console.log('[Onboarding Check] ⏸️ Conditions not met yet, waiting...');
      setShowOnboarding(false);
    }
  }, [currentUser, userProfile, legalAccepted]);

  const handleOnboardingComplete = () => {
    setShowOnboarding(false);
  };

  // If user is logged in but hasn't accepted legal terms, show legal acceptance
  if (currentUser && showLegalAcceptance && !legalAccepted) {
    return (
      <LegalAcceptance
        onAccept={handleLegalAcceptance}
        userEmail={currentUser.email}
      />
    );
  }

  // If showing landing page (for both logged-in and logged-out users)
  if (showLanding) {
    return (
      <LandingPage
        onSignIn={() => {
          setShowLanding(false);
          setAuthView('login');
        }}
        onStartTrial={() => {
          setShowLanding(false);
          setAuthView('signup');
        }}
        onCountrySelected={handleCountrySelected}
        onBackToApp={handleBackToApp}
        currentUser={currentUser}
        isAuthenticated={!!(currentUser && legalAccepted)}
      />
    );
  }

  // If user is logged in and has accepted legal terms
  if (currentUser && legalAccepted) {
    // Wait for user profile to load before proceeding
    if (!userProfile) {
      console.log('[App] Waiting for user profile to load...');
      return (
        <div className="min-h-screen bg-gradient-to-br from-blue-50 to-purple-50 flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Loading your profile...</p>
          </div>
        </div>
      );
    }

    // Show onboarding wizard for new users
    if (showOnboarding) {
      console.log('[App] Rendering onboarding wizard');
      return <OnboardingWizard onComplete={handleOnboardingComplete} />;
    }

    // Show main app for users who have completed onboarding
    console.log('[App] Rendering main app');
    return <InvestmentRetirementApp onNavigateToLanding={handleNavigateToLanding} />;
  }

  // If user is not logged in, show authentication screens
  return (
    <>
      {authView === 'login' && (
        <Login
          onSwitchToSignup={() => setAuthView('signup')}
          onSwitchToReset={() => setAuthView('reset')}
          onBackToLanding={handleNavigateToLanding}
        />
      )}
      {authView === 'signup' && (
        <Signup
          onSwitchToLogin={() => setAuthView('login')}
          selectedCountry={selectedCountryForAuth}
          onBackToLanding={handleNavigateToLanding}
        />
      )}
      {authView === 'reset' && (
        <PasswordReset
          onSwitchToLogin={() => setAuthView('login')}
          onBackToLanding={handleNavigateToLanding}
        />
      )}
    </>
  );
}

function AppContent() {
  return (
    <CountryProvider>
      <AppContentInner />
    </CountryProvider>
  );
}

function App() {
  // Set page title
  useEffect(() => {
    document.title = 'PlanLens - AI-Powered Wealth Building and Financial Planning';
  }, []);

  return (
    <BrowserRouter>
      <ThemeProvider>
        <AuthProvider>
          <Routes>
            {/* Legal Pages - Accessible without authentication */}
            <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
            <Route path="/terms" element={<TermsPage />} />
            <Route path="/terms-and-conditions" element={<TermsPage />} />

            {/* SEO Landing Pages - Accessible without authentication */}
            <Route path="/privacy-first-portfolio-tracker" element={<PrivacyFirstPortfolioTrackerPage />} />
            <Route path="/calculators/uk-pension-calculator" element={<UKPensionCalculatorPage />} />
            <Route path="/calculators/us-retirement-calculator" element={<USRetirementCalculatorPage />} />
            <Route path="/calculators/sipp-calculator-uk" element={<SippCalculatorPage />} />
            <Route path="/compare/financial-planner-vs-software" element={<FinancialPlannerVsSoftwarePage />} />

            {/* Main App */}
            <Route path="/*" element={<AppContent />} />
          </Routes>
        </AuthProvider>
      </ThemeProvider>
    </BrowserRouter>
  );
}

export default App;
